import { Card, ThemeToggle } from '@lumlab/platform-ui';
import type { ReactNode } from 'react';
import { BrandLogo } from './BrandLogo.tsx';

/** Frame of the screens reached without a session: sign-in, password reset, address confirmation. */
export function AuthScreen({ title, children }: { title: string; children: ReactNode }) {
    return (
        <div className="relative flex min-h-dvh flex-col items-center justify-center gap-8 p-4">
            <div className="absolute top-4 right-4">
                <ThemeToggle />
            </div>
            <BrandLogo layout="stacked" className="h-28 w-auto" />
            <Card padding="lg" className="w-full max-w-sm">
                <div className="flex flex-col gap-4">
                    <h1 className="text-2xl font-bold">{title}</h1>
                    {children}
                </div>
            </Card>
        </div>
    );
}

/**
 * Reads the secret parameters of an e-mail link from the URL fragment, which the browser never sends to a server,
 * then removes them from the address bar and the history.
 */
export function takeFragmentParameters(): URLSearchParams {
    const parameters = new URLSearchParams(window.location.hash.slice(1));
    if (window.location.hash) {
        window.history.replaceState(window.history.state, '', window.location.pathname + window.location.search);
    }
    return parameters;
}

/** Better Auth answers with its own codes; screens show the platform's stable ones. */
export function authErrorCode(error: { status: number; code?: string | undefined }): string {
    if (error.status === 429) return 'rate_limited';
    switch (error.code) {
        case 'INVALID_PASSWORD':
            return 'invalid_credentials';
        case 'INVALID_TOKEN':
            return 'invalid_link';
        case 'INVALID_CODE':
        case 'INVALID_BACKUP_CODE':
        case 'INVALID_OTP':
        case 'OTP_EXPIRED':
            return 'invalid_code';
        case 'TWO_FACTOR_REQUIRED':
            return 'two_factor_required';
        case 'TERMS_ACCEPTANCE_REQUIRED':
            return 'confirmation_terms_required';
        case 'FACTOR_REPLACEMENT_EXPIRED':
            return 'factor_replacement_expired';
        case 'RECENT_MFA_REQUIRED':
            return 'recent_mfa_required';
        case 'TOO_MANY_ATTEMPTS_REQUEST_NEW_CODE':
        case 'TOO_MANY_ATTEMPTS':
            return 'too_many_attempts';
        default:
            return error.status === 401 ? 'invalid_credentials' : 'internal';
    }
}
